import $ from 'zepto';

import { startsWith } from '../../utils/strings';
import { debounce, safeParseInt, getNewId } from '../../utils';
import BaseField from './BaseField';
import {
  SPACEBAR_KEYCODE,
  ENTER_KEYCODE,
  UP_ARROW_KEYCODE,
  DOWN_ARROW_KEYCODE,
  ESC_KEYCODE,
  TAB_KEYCODE,
} from '../../utils/constants';

import {
  DATA_USE_NATIVE,
  DATA_ICON_IN_OPTIONS,
  DATA_ICON,
  DATA_LABEL,
} from '../../utils/constants/dataKeys';

export const keys = [
  ENTER_KEYCODE,
  SPACEBAR_KEYCODE,
  UP_ARROW_KEYCODE,
  DOWN_ARROW_KEYCODE,
  ESC_KEYCODE,
  TAB_KEYCODE,
];

export const RESET_SEARCH_TIMER = 200;

const $window = $(window);

export default class SelectField extends BaseField {
  /**
   * @param {Zepto} field
   * @param {Zepto} select
   */
  constructor(baseClasses) {
    super(baseClasses);
    this.id = getNewId();
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onClickItem = this.onClickItem.bind(this);
    this.onFocusItem = this.onFocusItem.bind(this);
    this.onKeyDownItem = this.onKeyDownItem.bind(this);
    this.onBlurItem = this.onBlurItem.bind(this);
    this.onKeyPressItem = this.onKeyPressItem.bind(this);
    this.resetSearch = this.resetSearch.bind(this);
    this.onResize = debounce(this.onResize.bind(this), 150);
    this.onChange = this.onChange.bind(this);

    this.search = '';
  }

  get value() {
    return this.$select.val();
  }

  /**
   * creates the custom control and the list,
   * init the events and hide the select
   *
   * @param {Zepto} field
   * @param {Zepto} select
   * @returns {SelectField}
   */
  initSelectField(field, select) {
    if ($(select).attr('initialized') === '1') return this;
    this.$select = $(select);
    this.initField(field, {
      addEventHandlers: false,
    });
    this.iconInOptions = this.$field.data(DATA_ICON_IN_OPTIONS);
    this.useNativeList = this.$select.data(DATA_USE_NATIVE);

    if (this.isInGroupField) {
      this.$fieldGroup = this.$field.parent();
      this.isWideList = this.$fieldGroup.hasClass(this.fullWidthListClass);
    }

    const $selectedOption = this.getSelectedOption();

    this.$select.on('change', () => {
      if (this.getSelectedItem().data('value') !== this.$select.val()) {
        const $item = $(`${this.itemSelector} + [data-value="${this.$select.val()}"]`);

        this.getSelectedItem()
          .attr('aria-selected', false)
          .removeClass(this.selectedItemClass);

        $item
          .attr('aria-selected', true)
          .addClass(this.selectedItemClass);

        this.onChange();
      }

      this.$control.removeClass(this.customPlaceholderControlClass);
    });

    if (this.useNativeList) {
      this
        .createCustomControl($selectedOption);

      this.$select
        .focus(this.onFocusField)
        // SelectField is overriding BaseField onBlurField
        // but using the native select there is no need to
        // make anything special, so Field onBlurField is the correct one.
        .blur(super.onBlurField.bind(this))
        .on('change', this.onChange);
    } else if (!this.$select.prop('disabled')) {
      this
        .createCustomControl($selectedOption)
        .createList($selectedOption);

      this.$control
        .click(this.onClick)
        .keydown(this.onKeyDown)
        .keypress(this.onKeyPress)
        .focus(this.onFocusField)
        .blur(this.onBlurField);
    }

    this.$field.addClass(this.listFieldClass);
    this.$select.attr('initialized', '1');
    return this;
  }

  /**
   * get an item list that starts with an specific string
   *
   * @param {string} str
   * @returns {Array}
   */
  getItemsStartWith(str) {
    return Array.prototype.filter.call(
      this.$list.find(this.itemSelector),
      (item) => startsWith(item.textContent, str),
    );
  }

  /**
   * @returns {Zepto}
   */
  getFocusedItem() {
    return this.$list.find(this.focusItemSelector);
  }

  /**
   * @returns {Zepto}
   */
  getSelectedItem() {
    return this.$list.find(this.selectedItemSelector);
  }

  /**
   * determines if there is a value or not
   *
   * @returns {boolean}
   */
  isEmpty() {
    return !this.$select.val();
  }

  /**
   * determines if the field has a visible label
   *
   * @returns {boolean}
   */
  isLabelless() {
    return (
      this.$field.hasClass(this.labellessFieldClass)
      || this.$field.hasClass(this.smallFieldClass)
      || this.isInGroupField
    );
  }

  /**
   * determines if the given option is a placeholder
   *
   * @returns {boolean}
   */
  optionIsPlaceholder($option) {
    const firstOption = this.$select.get(0).options[0];

    return (
      $option.get(0) === firstOption
      && $option.prop('disabled')
    );
  }

  /**
   * Returns the selected option
   * @returns {Zepto}
   */
  getSelectedOption() {
    const select = this.$select.get(0);
    const option = select.options[select.selectedIndex];

    return $(option);
  }

  // #region actions
  /**
   * to change the control text and/or the icon
   *
   * @param {string} data.text
   * @param {string} [data.srcIcon]
   * @returns {SelectField}
   */
  updateControl(data) {
    const { text, srcIcon } = data;
    const $icon = this.$field.children(this.iconSelector);

    this.$control.text(text);

    // for now the only fields with icon are the label-less ones
    if (!this.isLabelless()) {
      return this;
    }

    if (srcIcon) {
      if ($icon.length === 1) {
        $icon.attr('src', srcIcon);
      } else {
        this.createFieldIcon(srcIcon);
      }
    } else {
      $icon.remove();
    }

    return this;
  }

  /**
   * changes the selected item
   *
   * @param {Zepto} $item
   * @returns {SelectField}
   */
  selectItem($item) {
    this.getSelectedItem()
      .attr('aria-selected', false)
      .removeClass(this.selectedItemClass);

    const label = $item.data(DATA_LABEL);

    $item
      .attr('aria-selected', true)
      .addClass(this.selectedItemClass);

    this.$select.val($item.data('value')).change();

    return this.updateControl({
      text: label || $item.text(),
      srcIcon: $item.data(DATA_ICON),
    });
  }

  /**
   * gives the focus to the control
   *
   * @returns {SelectField}
   */
  focusControl() {
    this.$control.focus();
    return this;
  }

  /**
   * gives the focus to a specific item
   *
   * @param {Zepto} $item
   * @returns {SelectField}
   */
  focusItem($item) {
    this.blurItem();
    $item
      .addClass(this.focusItemClass)
      .focus();

    return this;
  }

  /**
   * gives focus to the previous item
   *
   * @param {Zepto} $item current selected item
   * @returns {SelectField}
   */
  focusPrevItem($item) {
    const $prev = $item.prev();

    if ($prev.length === 0) {
      return this;
    }

    return this.focusItem($prev);
  }

  /**
   * gives focus to the next item
   *
   * @param {Zepto} $item current selected item
   * @returns {SelectField}
   */
  focusNextItem($item) {
    const $next = $item.next();

    if ($next.length === 0) {
      return this;
    }

    return this.focusItem($next);
  }

  /**
   * removes the focus to the item
   *
   * @returns {SelectField}
   */
  blurItem() {
    this
      .getFocusedItem()
      .removeClass(this.focusItemClass);

    return this;
  }

  /**
   * open the list, selecting the first element or the selected element
   *
   * @returns {SelectField}
   */
  openList() {
    this.$field.addClass(this.openListFieldClass);
    if (this.isWideList) {
      this.$fieldGroup.addClass(this.openWideListClass);
    }
    this.$list.prop('aria-expanded', true);
    let $item = this.getSelectedItem();
    if ($item.length === 0) {
      $item = this.$list.children().first();
    }
    this.focusItem($item);
    return this;
  }

  /**
   * close the list, removing the focus to the item
   *
   * @returns {SelectField}
   */
  closeList() {
    this.$list.prop('aria-expanded', true);
    this.blurItem();

    this.$field.removeClass(this.openListFieldClass);
    if (this.isWideList) {
      this.$fieldGroup.removeClass(this.openWideListClass);
    }
    return this;
  }

  /**
   * changes the list's status depending on the current status
   * and resets list width if a width of 0px was previously assigned to it
   * by the setPositionAndResizeWideList(). This happens when the component is
   * opened in a dialog window due to race conditions
   *
   * @returns {SelectField}
   */
  toggleList() {
    const listCssWidth = this.$list.css('width');
    if (listCssWidth === '0px') {
      this.setPositionAndResizeWideList();
    }
    if (this.$field.hasClass(this.openListFieldClass)) {
      this
        .closeList()
        .focusControl();
    } else {
      this.openList();
    }
    return this;
  }

  /**
   * place the select over all the other element
   * in the fields
   *
   * @returns {SelectField}
   */
  overlapSelect() {
    this.$select
      .removeClass()
      .css({
        opacity: 0,
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
      })
      // placing the field as the last children,
      // so it's over the other elements.
      .appendTo(this.$field);

    return this;
  }

  /**
   * hide the select and remove all its classes
   *
   * @returns {SelectField}
   */
  hideSelect() {
    this.$select
      .removeClass()
      .hide();

    return this;
  }

  /**
   * creates a new timer and clear a previous timer if exists
   *
   * @returns {SelectField}
   */
  setSearchTimer() {
    if (Number.isInteger(this.searchTimer)) {
      clearTimeout(this.searchTimer);
    }

    this.searchTimer = setTimeout(this.resetSearch, RESET_SEARCH_TIMER);
    return this;
  }

  /**
   * @returns {SelectField}
   */
  setPositionAndResizeWideList() {
    const fieldGroupWidth = this.$fieldGroup.width();

    this.$list.css('width', fieldGroupWidth);

    const lastElement = this.$fieldGroup.children().last().get(0);

    if (lastElement === this.$field.get(0)) {
      const groupBorderRightWidthPixels = this.$fieldGroup.css('border-right-width');
      const groupBorderLeftWidthPixels = this.$fieldGroup.css('border-left-width');

      const groupBorderRightWidth = safeParseInt(groupBorderRightWidthPixels, 10);
      const groupBorderLeftWidth = safeParseInt(groupBorderLeftWidthPixels, 10);

      const groupBorderSize = groupBorderRightWidth + groupBorderLeftWidth;

      const prevFieldWidth = this.$field.prev().width();

      // subtracting the prev field width, so
      // the list is aligned with the field group
      const left = -(prevFieldWidth + groupBorderSize);
      this.$list.css('left', left);
    }
    return this;
  }

  /**
   * resets the search if it is a word
   *
   * @returns {SelectField}
   */
  resetSearch() {
    if (this.search.length > 1) {
      this.search = '';
    }
    return this;
  }

  /**
   * @returns {SelectField}
   */
  selectMatch(char) {
    return this.listSearch(char, true);
  }

  /**
   * @returns {SelectField}
   */
  focusMatch(char) {
    return this.listSearch(char, false);
  }

  /**
   * selects or focus the first match or if there are no matches reset the search
   *
   * @param {string} char
   * @param {boolean} isSelect
   * @returns {SelectField}
   */
  listSearchFullWord(char, isSelect) {
    let search = this.search + char;
    const matches = this.getItemsStartWith(search);

    if (matches.length > 0) {
      if (isSelect) {
        this.selectItem($(matches[0]));
      } else {
        this.focusItem($(matches[0]));
      }
    } else if (matches.length === 0) {
      search = '';
    }
    this.search = search;

    return this;
  }

  /**
   * selects or focus the next match
   *
   * @param {string} char
   * @param {boolean} isSelect
   * @returns {SelectField}
   */
  listSearchNext(char, isSelect) {
    const $currentItem = isSelect ? this.getSelectedItem() : this.getFocusedItem();

    if (startsWith($currentItem.next().text(), char)) {
      if (isSelect) {
        this.selectItem($currentItem.next());
      } else {
        this.focusNextItem($currentItem);
      }
    }

    return this;
  }

  /**
   * selects or focus the first match
   *
   * @param {string} char
   * @param {boolean} isSelect
   * @returns {SelectField}
   */
  listSearchFirstMatch(char, isSelect) {
    const matches = this.getItemsStartWith(char);
    if (matches.length > 0) {
      if (isSelect) {
        this.selectItem($(matches[0]));
      } else {
        this.focusItem($(matches[0]));
      }
    }
    this.search = char;

    return this;
  }

  /**
   * selects or focus and decide which kind of search it is
   *
   * @param {string} char
   * @param {boolean} isSelect
   * @returns {SelectField}
   */
  listSearch(char, isSelect) {
    this.setSearchTimer();

    if (this.search.length === 0) {
      this.listSearchFirstMatch(char, isSelect);
    } else if (char.length === 1 && char === this.search) {
      this.listSearchNext(char, isSelect);
    } else {
      this.listSearchFullWord(char, isSelect);
    }

    return this;
  }

  /**
   * gives the focus to the field
   *
   * @override
   * @returns {Field}
   */
  focusField() {
    this.$field.addClass(this.focusFieldClass);

    if (this.isWideList) {
      this.$fieldGroup.addClass(this.focusWideListClass);
    }

    return this;
  }

  /**
   * removes the focus to the field
   *
   * @override
   * @returns {Field}
   */
  blurField() {
    this.$field.removeClass(this.focusFieldClass);

    if (this.isWideList) {
      this.$fieldGroup.removeClass(this.focusWideListClass);
    }
    return this;
  }
  // #endregion actions

  // #region creators
  /**
   * creates a custom control base on the select
   *
   * @returns {SelectField}
   */
  createCustomControl($selectedOption) {
    const classes = this.$select.prop('class');
    const text = $selectedOption.data(DATA_LABEL) || $selectedOption.text();

    let className = `${classes} ${this.customControlClass}`;

    if (this.optionIsPlaceholder($selectedOption)) {
      className = `${className} ${this.customPlaceholderControlClass}`;
    }

    this.$control = $('<div/>', {
      class: className,
      tabindex: 0,
      text,
      'aria-owns': this.id,
    }).appendTo(this.$field);

    const srcIcon = $selectedOption.data(DATA_ICON);

    if (this.isLabelless() && srcIcon) {
      this.createFieldIcon(srcIcon);
    }

    if (this.useNativeList) {
      this.$control.attr({
        tabindex: -1,
        'aria-hidden': true,
      });
      return this.overlapSelect();
    }

    return this.hideSelect();
  }

  /**
   * creates a list base on the select
   *
   * @returns {SelectField}
   */
  createList($selectedOption) {
    this.$list = $('<ul/>', {
      class: this.listClass,
      role: 'listbox',
      id: this.id,
      'aria-expanded': false,
    });
    this.$select.find('option').each((index, option) => {
      const isSelected = option === $selectedOption.get(0);
      this.createListItem(option, isSelected);
    });
    if (this.isWideList) {
      this.setPositionAndResizeWideList();
      $window.on('resize', this.onResize);
    }
    this.$field.append(this.$list);

    return this;
  }

  /**
   * creates each item base on an option
   *
   * @returns {SelectField}
   */
  createListItem(option, isSelected) {
    const { $list } = this;
    const $option = $(option);
    const value = $option.attr('value');
    const isOptionVisible = !$option.prop('hidden');

    if (!(value && isOptionVisible)) {
      return this;
    }

    const customLabel = this.isWideList ? $option.data(DATA_LABEL) : undefined;
    const text = $option.text();
    const iconSrc = $option.data(DATA_ICON);
    const isDisabled = $option.prop('disabled');
    const $li = $('<li/>', {
      class: `${this.itemClass} ${isSelected ? this.selectedItemClass : ''}`,
      role: 'option',
      tabindex: isDisabled ? -1 : 0,
      'aria-disabled': isDisabled,
      'aria-selected': isSelected,
      'data-value': value,
    });

    if (this.iconInOptions && iconSrc) {
      const $icon = $('<img/>', {
        src: iconSrc,
        class: this.iconItemClass,
        'aria-hidden': true,
      });

      $li.append($icon);
    }

    $li
      // saving the icon source to use it when an item is selected
      .data(DATA_ICON, iconSrc)
      .data(DATA_LABEL, customLabel)
      .append(text)
      .appendTo($list)
      .click(this.onClickItem)
      .focus(this.onFocusItem)
      .keypress(this.onKeyPressItem)
      .keydown(this.onKeyDownItem)
      .blur(this.onBlurItem);

    return this;
  }
  // #endregion creators

  // #region event handlers
  onKeyDownItem(evt) {
    if (keys.indexOf(evt.which) === -1) {
      return;
    }
    evt.preventDefault();
    evt.stopPropagation();

    const $item = $(evt.target);

    switch (evt.which) { // eslint-disable-line default-case
      case UP_ARROW_KEYCODE:
        this.focusPrevItem($item);
        break;
      case DOWN_ARROW_KEYCODE:
        this.focusNextItem($item);
        break;
      case ENTER_KEYCODE:
      case SPACEBAR_KEYCODE:
        this
          .selectItem($item)
          .closeList()
          .focusControl();
        break;
      case ESC_KEYCODE:
        this
          .closeList()
          .focusControl();
        break;
    }
  }

  onBlurItem(evt) {
    this.onBlurField(evt);
  }

  onFocusItem() {
    this.focusField();
  }

  /**
   * @override
   */
  onBlurField(evt) {
    const isStillInside = $(evt.relatedTarget).closest(this.fieldSelector).get(0) === this.$field.get(0);
    if (!isStillInside) {
      this
        .closeList()
        .closeField()
        .blurField();
    }
  }

  onKeyPressItem(evt) {
    const char = String.fromCharCode(evt.keyCode || evt.charCode);
    this.focusMatch(char);
  }

  onKeyPress(evt) {
    const char = String.fromCharCode(evt.keyCode || evt.charCode);
    this.selectMatch(char);
  }

  onClickItem(evt) {
    const $target = $(evt.target);

    // $target could be any children (like the icon) or the item itself
    const $item = $target.hasClass(this.itemClass)
      ? $target
      : $target.parent(this.itemSelector);

    this
      .selectItem($item)
      .closeList()
      .focusControl();

    evt.preventDefault();
    evt.stopPropagation();
  }

  onClick() {
    this.toggleList();
  }

  onKeyDown(evt) {
    switch (evt.which) { // eslint-disable-line default-case
      case UP_ARROW_KEYCODE:
      case DOWN_ARROW_KEYCODE:
      case SPACEBAR_KEYCODE:
        this.openList();
        evt.preventDefault();
        evt.stopPropagation();
        break;
    }
  }

  onResize() {
    this.setPositionAndResizeWideList();
  }

  onChange() {
    const [option] = this.$select.get(0).selectedOptions;
    const $option = $(option);

    this.updateControl({
      text: $option.data(DATA_LABEL) || $option.text(),
      srcIcon: $option.data(DATA_ICON),
    });
  }
  // #endregion event handlers
}
